import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { toast } from "sonner";
import { Loader2, Search, TrendingUp, Zap, Filter, AlertTriangle } from "lucide-react";

export default function IntentSignalsPanel() {
  const [demands, setDemands] = useState([]);
  const [stats, setStats] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cityFilter, setCityFilter] = useState("");
  const [queuingTarget, setQueuingTarget] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [dRes, sRes, tRes] = await Promise.all([
        axios.get(`${API}/admin/intent-signals/top-demands?limit=20`),
        axios.get(`${API}/admin/intent-signals/stats`),
        axios.get(`${API}/admin/intent-signals/trend?days=30`),
      ]);
      setDemands(dRes.data || []);
      setStats(sRes.data || {});
      setTrend(tRes.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const loadFiltered = async () => {
    try {
      const url = cityFilter
        ? `${API}/admin/intent-signals/top-demands?city=${encodeURIComponent(cityFilter)}&limit=20`
        : `${API}/admin/intent-signals/top-demands?limit=20`;
      const res = await axios.get(url);
      setDemands(res.data || []);
    } catch { /* ignore */ }
  };

  const queueTarget = async (demand) => {
    setQueuingTarget(demand.query);
    try {
      await axios.post(`${API}/admin/intent-signals/queue-target`, {
        target: demand.query,
        city: demand.city || cityFilter || "",
        type: demand.detected_business_name ? "turbo_research" : "category_priority",
      });
      toast.success(`Queued "${demand.query}" for targeted sweep`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Queue failed");
    } finally { setQueuingTarget(null); }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  const maxTrend = Math.max(1, ...trend.map(t => t.signals));

  return (
    <div className="space-y-4" data-testid="intent-signals-panel">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-2" data-testid="intent-stats">
          <div className="bg-sky-50 border border-sky-200 rounded-lg p-2.5 text-center">
            <p className="text-lg font-bold text-sky-700" data-testid="total-signals">{stats.total_signals}</p>
            <p className="text-[10px] text-sky-500">Total Signals</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-center">
            <p className="text-lg font-bold text-amber-700" data-testid="signals-24h">{stats.last_24h}</p>
            <p className="text-[10px] text-amber-500">Last 24h</p>
          </div>
          <div className="bg-violet-50 border border-violet-200 rounded-lg p-2.5 text-center">
            <p className="text-lg font-bold text-violet-700">{stats.unique_queries}</p>
            <p className="text-[10px] text-violet-500">Unique Queries</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 text-center">
            <p className="text-lg font-bold text-emerald-700">{stats.cities_with_signals}</p>
            <p className="text-[10px] text-emerald-500">Cities</p>
          </div>
        </div>
      )}

      {/* Trend chart (simple bar visualization) */}
      {trend.length > 0 && (
        <div data-testid="intent-trend">
          <p className="text-[11px] font-semibold text-slate-500 mb-2">Demand trend (30d)</p>
          <div className="flex items-end gap-px h-12">
            {trend.slice(-30).map((t, i) => (
              <div key={i} className="flex-1 min-w-[3px] bg-sky-400 rounded-t-sm opacity-80 hover:opacity-100 transition"
                style={{ height: `${Math.max(4, (t.signals / maxTrend) * 100)}%` }}
                title={`${t.date}: ${t.signals} signals`} />
            ))}
          </div>
        </div>
      )}

      {/* City filter */}
      <div className="flex items-center gap-2" data-testid="city-filter">
        <Filter className="w-3.5 h-3.5 text-slate-400" />
        <input
          type="text"
          value={cityFilter}
          onChange={(e) => setCityFilter(e.target.value)}
          placeholder="Filter by city..."
          className="flex-1 text-xs bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 outline-none focus:border-sky-300"
          data-testid="city-filter-input"
        />
        <button onClick={loadFiltered}
          className="px-2.5 py-1.5 bg-sky-500 text-white text-[11px] font-semibold rounded-lg hover:bg-sky-600 transition"
          data-testid="filter-btn">
          Filter
        </button>
      </div>

      {/* Top unmet demands */}
      {demands.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-4">No intent signals recorded yet. Signals appear when users search with zero/low results.</p>
      ) : (
        <div className="space-y-1.5 max-h-80 overflow-y-auto" data-testid="demands-list">
          {demands.map((d, i) => (
            <div key={`${d.query}-${i}`}
              className="flex items-center gap-2 p-2.5 bg-white border border-slate-200 rounded-lg"
              data-testid={`demand-item-${i}`}>
              <Search className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-800 truncate">{d.query}</span>
                  <span className="text-[10px] font-bold text-sky-600 bg-sky-50 px-1.5 py-0.5 rounded">{d.search_count}x</span>
                  {d.detected_category && (
                    <span className="text-[9px] px-1.5 py-0.5 bg-violet-50 text-violet-600 rounded-full">{d.detected_category}</span>
                  )}
                  {d.detected_dietary && (
                    <span className="text-[9px] px-1.5 py-0.5 bg-emerald-50 text-emerald-600 rounded-full">{d.detected_dietary}</span>
                  )}
                  {!d.detected_category && !d.detected_dietary && d.detected_business_name && (
                    <span className="text-[9px] px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded-full">biz name</span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-400">
                  {d.city && <span>{d.city}</span>}
                  <span>avg {d.avg_result_count} results</span>
                  {d.last_searched_at && <span>{d.last_searched_at.slice(0, 10)}</span>}
                </div>
              </div>
              <button onClick={() => queueTarget(d)} disabled={queuingTarget === d.query}
                className="px-2 py-1 bg-sky-50 text-sky-600 text-[10px] font-semibold rounded hover:bg-sky-100 transition flex-shrink-0 flex items-center gap-1"
                data-testid={`queue-btn-${i}`}>
                {queuingTarget === d.query ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                Sweep
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
